import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  chatWithAgent,
  getMessageHistory,
  getActiveSessions,
  aiAgentKeys,
  ChatRequest,
  ChatResponse,
  AIAgentMessage,
  MessageHistoryParams,
  ActiveSessionInfo,
  
  // Legacy functions for backward compatibility
  createAgentSession,
  getOrCreateAgentSession,
  getAgentSession,
  updateAgentState,
  sendAgentMessage,
  getAgentSessionMessages,
  AgentSession,
  AgentMessage,
  CreateSessionRequest,
  UpdateAgentStateRequest,
  SendMessageRequest,
  getAiAgentPrompts,
  getPastIdeaSessions,
  getSessionMessages
} from '../api/aiAgent';
import { ActiveFilter } from '../pages/filter-bar/FilterBadges';
import posthog from 'posthog-js';
import _ from "lodash";

// ===== NEW HOOKS BASED ON BACKEND README =====

/**
 * Hook for AI Agent chat functionality using the new chat endpoint
 */
export interface AIAgentChatRecoveryHooks {
  onSendStart?: () => void;
  onNeedsRecovery?: () => void;
}

export type AIAgentChatMode = 'validate';

export interface AIAgentSendMessageOptions {
  mode?: AIAgentChatMode;
}

type AIAgentChatMutationInput = {
  message: string;
  mode?: AIAgentChatMode;
};

export const useAIAgentChat = (
  ideaId: string,
  sessionId: string,
  activeFilters?: ActiveFilter[],
  recoveryHooks?: AIAgentChatRecoveryHooks,
  chatOptions?: { mode?: AIAgentChatMode }
) => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  const mode = chatOptions?.mode;

  const chatMutation = useMutation({
    mutationFn: async ({ message, mode: messageMode }: AIAgentChatMutationInput) => {
      recoveryHooks?.onSendStart?.();
      const requestMode = messageMode ?? mode;
      const sessionIdForRequest = sessionId || '';
      const request: ChatRequest = {
        message,
        activeFilters: activeFilters || [], // Always send activeFilters, default to empty array
        ...(requestMode ? { mode: requestMode } : {}),
        sessionId: sessionIdForRequest,
      };
      return await chatWithAgent(ideaId, request, auth0);
    },
    onMutate: async ({ message, mode: messageMode }: AIAgentChatMutationInput) => {
      console.log('📝 [AI Agent Chat Hook] Optimistic update - adding user message');
      const optimisticMode = messageMode ?? mode;

      const messageQueryKey = [...aiAgentKeys.ideaMessages(ideaId), 'session', sessionId || ''];

      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({
        queryKey: messageQueryKey,
      });

      // Snapshot the previous value for rollback
      const previousMessages = queryClient.getQueryData(messageQueryKey);

      // Create optimistic user message
      const optimisticSessionId = sessionId || `temp-session-${Date.now()}`;
      
      const optimisticUserMessage: AIAgentMessage = {
        _id: `temp-${Date.now()}`,
        accountId: auth0.user?.sub || '',
        sessionId: optimisticSessionId,
        userId: auth0.user?.sub || '',
        ideaId: ideaId,
        messageType: 'user',
        content: message,
        timestamp: new Date(),
        ...(optimisticMode ? { mode: optimisticMode } : {}),
      };

      // Optimistically update the messages cache
      queryClient.setQueryData(messageQueryKey, (old: any) => {
        if (!old) {
          // If no old data, create a new structure
          return {
            pages: [{
              messages: [optimisticUserMessage],
              success: true,
              hasMore: false
            }],
            pageParams: [undefined]
          };
        }

        // Add optimistic message to the first page
        // Messages come from API in reverse chronological (newest first), then get sorted chronologically
        // So we add to the beginning of the first page, which will appear at the end after sorting
        const newPages = [...old.pages];
        if (newPages.length > 0) {
          newPages[0] = {
            ...newPages[0],
            messages: [optimisticUserMessage, ...(newPages[0].messages || [])]
          };
        } else {
          newPages.push({
            messages: [optimisticUserMessage],
            success: true,
            hasMore: false
          });
        }

        return {
          ...old,
          pages: newPages
        };
      });

      // Return context with snapshot for potential rollback
      return { previousMessages };
    },
    onError: (error: Error) => {
      console.error('❌ [AI Agent Chat Hook] Failed to send message:', error.message);

      // The HTTP call failed (e.g. 504 from GCP LB at ~60s). The backend may
      // still finish and persist messages, so trigger recovery polling and
      // refetch so we surface whatever lands.
      recoveryHooks?.onNeedsRecovery?.();

      queryClient.invalidateQueries({ 
        queryKey: aiAgentKeys.ideaMessages(ideaId) 
      });
    },
    onSuccess: (response: ChatResponse) => {
      // `chatWithAgent` swallows errors and returns { success: false }. Treat
      // that the same as a thrown error: trigger recovery, don't pollute
      // analytics with an empty sessionId.
      if (!response?.success) {
        console.warn('⚠️ [AI Agent Chat Hook] Response indicates failure, starting recovery');
        recoveryHooks?.onNeedsRecovery?.();
        queryClient.invalidateQueries({ 
          queryKey: aiAgentKeys.ideaMessages(ideaId) 
        });
        queryClient.invalidateQueries({ 
          queryKey: aiAgentKeys.activeSessions(ideaId) 
        });
        return;
      }

      console.log('✅ [AI Agent Chat Hook] Message sent successfully:', response.sessionId);
      // Analytics: capture with relevant sessionId returned by the API

      posthog.capture('ask_bagel_ai_message_sent', {
        sessionId: response.sessionId,
      });
      
      // Invalidate and refetch message history to get the real messages from server
      // This will replace the optimistic update with actual data
      queryClient.invalidateQueries({ 
        queryKey: aiAgentKeys.ideaMessages(ideaId) 
      });
      
      // Invalidate active sessions
      queryClient.invalidateQueries({ 
        queryKey: aiAgentKeys.activeSessions(ideaId) 
      });
    },
    onSettled: () => {
      // Ensure we're in sync with server after mutation completes
      queryClient.invalidateQueries({ 
        queryKey: aiAgentKeys.ideaMessages(ideaId) 
      });
    }
  });
  
  return {
    sendMessage: (message: string, sendOptions?: AIAgentSendMessageOptions) =>
      chatMutation.mutateAsync({
        message,
        mode: sendOptions?.mode ?? mode,
      }),
    isSending: chatMutation.isPending,
    error: chatMutation.error?.message || null,
    reset: chatMutation.reset,
  };
};

export const useAIAgentChatWithSessionId = (ideaId: string, sessionId: string, activeFilters?: ActiveFilter[]) => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  
  // Chat mutation using the new endpoint with optimistic updates
  const chatMutation = useMutation({
    mutationFn: async (message: string) => {
      const request: ChatRequest = { 
        message,
        sessionId,
        activeFilters: activeFilters || [] // Always send activeFilters, default to empty array
      };
      // Always send by idea; if sessionId provided, also send by session
      
      return await chatWithAgent(ideaId, request, auth0);
    },
    onMutate: async (message: string) => {
      console.log('📝 [AI Agent Chat Hook] Optimistic update - adding user message');
      
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ 
        queryKey: aiAgentKeys.ideaMessages(ideaId) 
      });

      // Snapshot the previous value for rollback
      const previousMessages = queryClient.getQueryData(aiAgentKeys.ideaMessages(ideaId));

      // Create optimistic user message using provided sessionId
      const optimisticSessionId = sessionId || `temp-session-${Date.now()}`;
      
      const optimisticUserMessage: AIAgentMessage = {
        _id: `temp-${Date.now()}`,
        accountId: auth0.user?.sub || '',
        sessionId: optimisticSessionId,
        userId: auth0.user?.sub || '',
        ideaId: ideaId,
        messageType: 'user',
        content: message,
        timestamp: new Date(),
      };

      // Optimistically update the messages cache
      queryClient.setQueryData(aiAgentKeys.ideaMessages(ideaId), (old: any) => {
        if (!old) {
          // If no old data, create a new structure
          return {
            pages: [{
              messages: [optimisticUserMessage],
              success: true,
              hasMore: false
            }],
            pageParams: [undefined]
          };
        }

        // Add optimistic message to the first page
        // Messages come from API in reverse chronological (newest first), then get sorted chronologically
        // So we add to the beginning of the first page, which will appear at the end after sorting
        const newPages = [...old.pages];
        if (newPages.length > 0) {
          newPages[0] = {
            ...newPages[0],
            messages: [optimisticUserMessage, ...(newPages[0].messages || [])]
          };
        } else {
          newPages.push({
            messages: [optimisticUserMessage],
            success: true,
            hasMore: false
          });
        }

        return {
          ...old,
          pages: newPages
        };
      });

      // Return context with snapshot for potential rollback
      return { previousMessages };
    },
    onError: (error: Error, message: string, context: any) => {
      console.error('❌ [AI Agent Chat Hook] Failed to send message:', error.message);
      
      // Instead of just rolling back, refetch messages to get actual saved messages from server
      // This ensures user message that was saved is shown even if agent response failed
      queryClient.invalidateQueries({ 
        queryKey: aiAgentKeys.ideaMessages(ideaId) 
      });
    },
    onSuccess: (response: ChatResponse) => {
      console.log('✅ [AI Agent Chat Hook] Message sent successfully:', response.sessionId);
      // Analytics: capture with relevant sessionId returned by the API

      posthog.capture('ask_bagel_ai_message_sent', {
        sessionId: response.sessionId,
      });
      
      // Invalidate and refetch message history to get the real messages from server
      // This will replace the optimistic update with actual data
      queryClient.invalidateQueries({ 
        queryKey: aiAgentKeys.ideaMessages(ideaId) 
      });
      
      // Invalidate active sessions
      queryClient.invalidateQueries({ 
        queryKey: aiAgentKeys.activeSessions(ideaId) 
      });

      // If a sessionId was provided, also refresh that session's messages
      if (sessionId && sessionId !== '') {
        queryClient.invalidateQueries({ 
          queryKey: aiAgentKeys.sessionMessages(sessionId) 
        });
      } else if (response?.sessionId) {
        // Also invalidate the session we just received from backend (first message)
        queryClient.invalidateQueries({ 
          queryKey: aiAgentKeys.sessionMessages(response.sessionId) 
        });
      }
    },
    onSettled: () => {
      // Ensure we're in sync with server after mutation completes
      queryClient.invalidateQueries({ 
        queryKey: aiAgentKeys.ideaMessages(ideaId) 
      });
    }
  });
  
  return {
    sendMessage: chatMutation.mutateAsync,
    isSending: chatMutation.isPending,
    error: chatMutation.error?.message || null,
    reset: chatMutation.reset,
  };
};

/**
 * Hook for retrieving message history with infinite scroll support
 * @param ideaId - The idea ID to fetch messages for
 * @param options.enabled - Whether to enable the query
 * @param options.refetchInterval - Custom refetch interval (false to disable polling)
 * @param options.messageModeFilter - `'validate'` shows only validation-helper messages; `'default'` hides them so Ask Bagel and Validation can share one session id.
 */
export const useAIAgentMessages = (
  ideaId: string,
  sessionId: string,
  options: {
    enabled?: boolean;
    refetchInterval?: number | false;
    messageModeFilter?: 'validate' | 'default';
  } = {}
) => {
  const auth0 = useAuth0();
  const messageModeFilter = options.messageModeFilter ?? 'default';

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch
  } = useInfiniteQuery({
    queryKey: [...aiAgentKeys.ideaMessages(ideaId), 'session', sessionId],
    queryFn: async ({ pageParam }) => {

      const params: MessageHistoryParams = {
        limit: 20,
        
      };
      return await getMessageHistory(ideaId, sessionId ,params, auth0);
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage: any) => {
      // If we have messages and potentially more, use the oldest message timestamp as cursor
      if (lastPage?.messages && lastPage.messages.length > 0 && lastPage.hasMore) {
        const oldestMessage = lastPage.messages[lastPage.messages.length - 1];
        return oldestMessage.timestamp;
      }
      return undefined;
    },
    enabled: !!ideaId && options.enabled !== false,
    staleTime: 30 * 1000, // 30 seconds
    // Smart polling: only poll when refetchInterval is provided, otherwise rely on window focus
    refetchInterval: options.refetchInterval ?? false,
    refetchOnWindowFocus: true,
  });
  
  // Flatten all messages from all pages, dedupe by _id (overlapping pages), sort oldest first
  const rawSorted = useMemo(() => {
    const raw =
      data?.pages?.flatMap((page: any) => page?.messages || []) || [];
    const seen = new Set<string>();
    const deduped: any[] = [];
    for (const m of raw) {
      const k = m?._id != null ? String(m._id) : null;
      if (k) {
        if (seen.has(k)) continue;
        seen.add(k);
      }
      deduped.push(m);
    }
    return deduped.sort(
      (a: any, b: any) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }, [data]);

  const messages = useMemo(() => {
    return rawSorted.filter((m: any) => {
      if (messageModeFilter === 'validate') return m?.mode === 'validate';
      return m?.mode !== 'validate';
    });
  }, [rawSorted, messageModeFilter]);

  const firstSessionId = useMemo(() => {
    for (const m of rawSorted) {
      const sid = m?.sessionId;
      if (typeof sid === 'string' && sid && !sid.startsWith('temp-session-')) {
        return sid;
      }
    }
    return '';
  }, [rawSorted]);
  
  return {
    messages,
    firstSessionId,
    isLoading,
    error: error?.message || null,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};

/**
 * Hook for getting active sessions for an idea
 */
export const useActiveSessions = (ideaId: string, options: { enabled?: boolean } = {}) => {
  const auth0 = useAuth0();
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: aiAgentKeys.activeSessions(ideaId),
    queryFn: async () => {
      return await getActiveSessions(ideaId, auth0);
    },
    enabled: !!ideaId && options.enabled !== false,
    staleTime: 60 * 1000, // 1 minute
    // No constant polling - use window focus instead
    refetchInterval: false,
    refetchOnWindowFocus: true,
  });
  
  return {
    sessions: data?.sessions || [],
    sessionCount: data?.sessionCount || 0,
    isLoading,
    error: error?.message || null,
    refetch,
  };
};

export const usePrompts = (type: 'idea' | 'discovery', options: { enabled?: boolean } = {}) => {
  const auth0 = useAuth0();
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: aiAgentKeys.prompts(),
    queryFn: async () => {
      return await getAiAgentPrompts(auth0, type);
    },
    enabled: options.enabled !== false,
    staleTime: 60 * 1000, // 1 minute
    // No constant polling - use window focus instead
    refetchInterval: false,
    refetchOnWindowFocus: true,
  });

  return {
    prompts: data?.prompts || [],
    isLoadingPrompts: isLoading,
    error: error?.message || null,
    refetch,
  };
};

export const usePastSessions = (ideaId: string) => {
  const auth0 = useAuth0();
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: aiAgentKeys.pastSessions(ideaId),
    queryFn: async () => {
      return await getPastIdeaSessions(ideaId, auth0);
    },
    enabled: !!ideaId,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
  });
  
  return {
    sessions: data?.sessions || [],
    sessionCount: data?.sessionCount || 0,
    isLoading,
    error: error?.message || null,
    refetch,
  };
};


/* export const useMessagesBySessionId = (sessionId: string, options: { enabled?: boolean } = {}) => {
  const auth0 = useAuth0();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch
  } = useInfiniteQuery({
    queryKey: aiAgentKeys.sessionMessages(sessionId),
    queryFn: async ({ pageParam }) => {
      const params: MessageHistoryParams = {
        limit: 20,
        ...(pageParam && { before: pageParam as string })
      };
      return await getSessionMessages(sessionId, params, auth0);
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.messages && lastPage.messages.length > 0 && lastPage.hasMore) {
        const oldestMessage = lastPage.messages[lastPage.messages.length - 1];
        return oldestMessage.timestamp;
      }
      return undefined;
    },
    enabled: !!sessionId && options.enabled !== false,
    staleTime: 30 * 1000,
    refetchInterval: 10 * 1000,
  });

  const messages = data?.pages
    .flatMap((page: any) => page.messages)
    .sort((a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()) || [];

  return {
    messages,
    isLoading,
    error: error?.message || null,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
}; */

/**
 * Comprehensive AI Agent hook that combines chat, messages, and session management
 */
// How long to keep polling for the agent reply after a failed/timed-out send,
// and how often to poll. Covers the common case where the GCP LB cut the
// connection at ~60s but the backend kept running and will persist the
// reply shortly after.
const RECOVERY_WINDOW_MS = 120 * 1000;
const RECOVERY_POLL_INTERVAL_MS = 3000;

export const useAIAgent = (
  ideaId: string,
  sessionId: string,
  activeFilters?: ActiveFilter[],
  options?: { mode?: AIAgentChatMode }
) => {
  const panelMode = options?.mode;
  // `recoveryUntil` is a timestamp (ms). When > Date.now(), we are polling
  // messages in recovery mode. `sendStartedAtRef` tracks when the most recent
  // send started so we can detect a "fresh" agent message and stop polling.
  const [recoveryUntil, setRecoveryUntil] = useState<number>(0);
  const sendStartedAtRef = useRef<number>(0);

  const handleSendStart = useCallback(() => {
    sendStartedAtRef.current = Date.now();
  }, []);

  const handleNeedsRecovery = useCallback(() => {
    setRecoveryUntil(Date.now() + RECOVERY_WINDOW_MS);
  }, []);

  const chat = useAIAgentChat(ideaId, sessionId, activeFilters,
    { 
      onSendStart: handleSendStart,
      onNeedsRecovery: handleNeedsRecovery,
    },
    { mode: options?.mode }
  );

  const isRecovering = recoveryUntil > 0 && Date.now() < recoveryUntil;

  const messages = useAIAgentMessages(ideaId, sessionId, {
    refetchInterval: isRecovering ? RECOVERY_POLL_INTERVAL_MS : false,
    messageModeFilter: panelMode === 'validate' ? 'validate' : 'default',
  });

  // Stop recovery as soon as we see an agent message that was written after
  // the user's send (i.e. the late reply has landed).
  useEffect(() => {
    if (!isRecovering) return;
    const sendStartedAt = sendStartedAtRef.current;
    if (!sendStartedAt) return;
    const hasFreshAgentMsg = (messages.messages || []).some((m: any) =>
      m?.messageType === 'agent' &&
      new Date(m.timestamp).getTime() > sendStartedAt &&
      (panelMode !== 'validate' || m?.mode === 'validate')
    );
    if (hasFreshAgentMsg) {
      setRecoveryUntil(0);
    }
  }, [isRecovering, messages.messages, panelMode]);

  // Hard-cap: clear recovery when the window expires even if no reply arrived.
  useEffect(() => {
    if (recoveryUntil === 0) return;
    const ms = recoveryUntil - Date.now();
    if (ms <= 0) {
      setRecoveryUntil(0);
      return;
    }
    const t = setTimeout(() => setRecoveryUntil(0), ms);
    return () => clearTimeout(t);
  }, [recoveryUntil]);

  /* const activeSessions = useActiveSessions(ideaId); */
  
  return {
    // Chat functionality
    sendMessage: (message: string, sendOptions?: AIAgentSendMessageOptions) =>
      chat.sendMessage(message, sendOptions),
    isSending: chat.isSending,
    sendError: chat.error,
    isRecovering,
    
    // Messages
    messages: messages.messages,
    firstSessionId: messages.firstSessionId,
    isLoadingMessages: messages.isLoading,
    messagesError: messages.error,
    fetchMoreMessages: messages.fetchNextPage,
    hasMoreMessages: messages.hasNextPage,
    isFetchingMore: messages.isFetchingNextPage,
    refreshMessages: messages.refetch,
    
    // Sessions
    /* activeSessions: activeSessions.sessions,
    sessionCount: activeSessions.sessionCount,
    isLoadingSessions: activeSessions.isLoading,
    sessionsError: activeSessions.error,
    refreshSessions: activeSessions.refetch,  */
  };
};

// ===== LEGACY HOOKS FOR BACKWARD COMPATIBILITY =====

export interface UseAiAgentReturn {
  // Session management
  session: AgentSession | null;
  isLoadingSession: boolean;
  sessionError: string | null;
  
  // Messages
  messages: AgentMessage[];
  isLoadingMessages: boolean;
  messagesError: string | null;
  
  // Actions
  initializeSession: (ideaId: string, userId: string) => Promise<void>;
  updateState: (ideaId: string, accountId: string, activeFilters?: Record<string, any>) => Promise<void>;
  sendMessage: (message: string) => Promise<void>;
  refreshMessages: () => Promise<void>;
  
  // Message sending state
  isSendingMessage: boolean;
  sendMessageError: string | null;
  
  // Clear functions
  clearSession: () => void;
  clearMessages: () => void;
}

export const useAiAgent = (ideaId?: string): UseAiAgentReturn => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  
  // Local state
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [sendMessageError, setSendMessageError] = useState<string | null>(null);

  // Session query
  const {
    data: session,
    isLoading: isLoadingSession,
    error: sessionError,
    refetch: refetchSession
  } = useQuery({
    queryKey: ['aiAgentSession', currentSessionId],
    queryFn: async () => {
      if (!currentSessionId) return null;
      const response = await getAgentSession(currentSessionId, auth0);
      if (!response.success) {
        throw new Error(response.message || 'Failed to get session');
      }
      return response.session;
    },
    enabled: !!currentSessionId,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Messages query
  const {
    data: sessionMessages,
    isLoading: isLoadingMessages,
    error: messagesError,
    refetch: refetchMessages
  } = useQuery({
    queryKey: ['aiAgentMessages', currentSessionId],
    queryFn: async () => {
      if (!currentSessionId) return [];
      const response = await getAgentSessionMessages(currentSessionId, auth0);
      if (!response.success) {
        throw new Error(response.message || 'Failed to get messages');
      }
      return response.messages;
    },
    enabled: !!currentSessionId,
    retry: 1,
    staleTime: 30 * 1000, // 30 seconds
    // No constant polling - use window focus instead
    refetchInterval: false,
    refetchOnWindowFocus: true,
  });

  // Update local messages when session messages change
  useEffect(() => {
    if (sessionMessages) {
      console.log('📥 [AI Agent Hook] Updating messages from session:', sessionMessages.length);
      setMessages(sessionMessages);
    }
  }, [sessionMessages]);

  // Initialize session mutation
  const initializeSessionMutation = useMutation({
    mutationFn: async ({ ideaId, userId }: { ideaId: string; userId: string }) => {
      console.log('🚀 [AI Agent Hook] Initializing session for idea:', ideaId);
      
      const request: CreateSessionRequest = { ideaId, userId };
      const response = await getOrCreateAgentSession(request, auth0);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to initialize session');
      }
      
      return response.session;
    },
    onSuccess: (session) => {
      console.log('✅ [AI Agent Hook] Session initialized:', session.sessionId);
      setCurrentSessionId(session.sessionId);
      setSendMessageError(null);
    },
    onError: (error: Error) => {
      console.error('❌ [AI Agent Hook] Failed to initialize session:', error.message);
      setSendMessageError(error.message);
    }
  });

  // Update state mutation
  const updateStateMutation = useMutation({
    mutationFn: async ({ 
      ideaId, 
      accountId, 
      activeFilters 
    }: { 
      ideaId: string; 
      accountId: string; 
      activeFilters?: Record<string, any> 
    }) => {
      if (!currentSessionId) {
        throw new Error('No active session');
      }
      
      console.log('🔄 [AI Agent Hook] Updating agent state for session:', currentSessionId);
      
      const request: UpdateAgentStateRequest = {
        idea_id: ideaId,
        account_id: accountId,
        active_filters: activeFilters
      };
      
      const response = await updateAgentState(currentSessionId, request, auth0);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update agent state');
      }
      
      return response;
    },
    onSuccess: () => {
      console.log('✅ [AI Agent Hook] Agent state updated successfully');
    },
    onError: (error: Error) => {
      console.error('❌ [AI Agent Hook] Failed to update agent state:', error.message);
    }
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (message: string) => {
      if (!currentSessionId) {
        throw new Error('No active session');
      }
      
      console.log('💬 [AI Agent Hook] Sending message to session:', currentSessionId);
      
      const request: SendMessageRequest = { message };
      const response = await sendAgentMessage(currentSessionId, request, auth0);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to send message');
      }
      
      return response;
    },
    onSuccess: (response) => {
      console.log('✅ [AI Agent Hook] Message sent successfully');
      setSendMessageError(null);
      
      // Refresh messages to get the latest conversation
      refetchMessages();
    },
    onError: (error: Error) => {
      console.error('❌ [AI Agent Hook] Failed to send message:', error.message);
      setSendMessageError(error.message);
    }
  });

  // Initialize session function
  const initializeSession = useCallback(async (ideaId: string, userId: string) => {
    console.log('🎬 [AI Agent Hook] Initializing session...');
    await initializeSessionMutation.mutateAsync({ ideaId, userId });
  }, [initializeSessionMutation]);

  // Update state function
  const updateState = useCallback(async (
    ideaId: string, 
    accountId: string, 
    activeFilters?: Record<string, any>
  ) => {
    if (!currentSessionId) {
      console.warn('⚠️ [AI Agent Hook] No active session to update state');
      return;
    }
    
    console.log('🔄 [AI Agent Hook] Updating state...');
    await updateStateMutation.mutateAsync({ ideaId, accountId, activeFilters });
  }, [currentSessionId, updateStateMutation]);

  // Send message function
  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim()) {
      console.warn('⚠️ [AI Agent Hook] Attempted to send empty message');
      return;
    }
    
    if (!currentSessionId) {
      console.error('❌ [AI Agent Hook] No active session');
      setSendMessageError('No active session. Please initialize a session first.');
      return;
    }
    
    console.log('🚀 [AI Agent Hook] Sending message...');
    await sendMessageMutation.mutateAsync(message.trim());
  }, [currentSessionId, sendMessageMutation]);

  // Refresh messages function
  const refreshMessages = useCallback(async () => {
    console.log('🔄 [AI Agent Hook] Refreshing messages...');
    await refetchMessages();
  }, [refetchMessages]);

  // Clear session function
  const clearSession = useCallback(() => {
    console.log('🧹 [AI Agent Hook] Clearing session');
    setCurrentSessionId(null);
    setMessages([]);
    setSendMessageError(null);
    queryClient.removeQueries({ queryKey: ['aiAgentSession'] });
    queryClient.removeQueries({ queryKey: ['aiAgentMessages'] });
  }, [queryClient]);

  // Clear messages function
  const clearMessages = useCallback(() => {
    console.log('🧹 [AI Agent Hook] Clearing messages');
    setMessages([]);
    setSendMessageError(null);
  }, []);

  return {
    // Session management
    session: session || null,
    isLoadingSession,
    sessionError: sessionError?.message || null,
    
    // Messages
    messages,
    isLoadingMessages,
    messagesError: messagesError?.message || null,
    
    // Actions
    initializeSession,
    updateState,
    sendMessage,
    refreshMessages,
    
    // Message sending state
    isSendingMessage: sendMessageMutation.isPending,
    sendMessageError,
    
    // Clear functions
    clearSession,
    clearMessages,
  };
};